import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeller } from '../context/SellerContext';
import { useCategories } from '../context/CategoryContext';
import { getCategorySettings, getCommonPolicyByType } from '../services/api';
import { sanitizeHtml } from '../utils/sanitize';
import Modal from './common/Modal';
import TermsModal from './TermsModal';
import SellerBusinessInfo from './SellerBusinessInfo';

function SellerFooter() {
  const navigate = useNavigate();
  const { subdomain, shopName, seller: initialSellerInfo } = useSeller();
  const { categories: ctxCategories } = useCategories();
  const [sellerInfo, setSellerInfo] = useState(initialSellerInfo || {});
  const [termsOpen, setTermsOpen] = useState(false);
  const [sortedCategories, setSortedCategories] = useState([]);
  const [policyModal, setPolicyModal] = useState({ isOpen: false, title: '', content: '' });
  const [csInfo, setCsInfo] = useState({ phone: '010-3782-8997', operatingHours: '평일 09:00 - 18:00 (주말/공휴일 휴무)' });

  useEffect(() => {
    if (initialSellerInfo) {
      setSellerInfo(initialSellerInfo);
    }
  }, [initialSellerInfo]);

  // 고객센터 정보 로드
  useEffect(() => {
    const loadCs = async () => {
      try {
        const res = await getCommonPolicyByType('CUSTOMER_SERVICE');
        if (res.data?.content) {
          const data = JSON.parse(res.data.content);
          setCsInfo(data);
        }
      } catch (e) { /* keep default */ }
    };
    loadCs();
  }, []);

  // CategoryContext에서 활성 카테고리 (displayOrder 순)
  useEffect(() => {
    if (ctxCategories.length > 0) {
      const cats = ctxCategories
        .filter(c => !c.isHot)
        .map(cat => ({ name: cat.name || cat.title, value: cat.categoryValue, displayOrder: cat.displayOrder || 999 }));
      cats.sort((a, b) => a.displayOrder - b.displayOrder);
      setSortedCategories(cats);
    }
  }, [ctxCategories]);

  const logoUrl = sellerInfo?.logoUrl;
  const displayName = sellerInfo?.shopName || shopName || "플라워 플랫폼";

  /* --- Mobile Accordion Helper --- */
  const MobileFooterAccordion = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
      <div className="border-b border-gray-100 dark:border-gray-800 last:border-0 md:border-0">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between py-4 md:py-0 md:cursor-default md:pointer-events-none"
        >
          <h3 className="font-bold text-sm dark:text-white">{title}</h3>
          <span
            className={`material-symbols-outlined text-gray-400 text-sm transition-transform ${isOpen ? 'rotate-180' : ''} footer-accordion-arrow`}
          >
            expand_more
          </span>
        </button>
        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px] opacity-100 mb-4' : 'max-h-0 opacity-0 md:max-h-none md:opacity-100 md:mb-0'}`}>
           {children}
        </div>
      </div>
    );
  };

  const handleCategoryClick = (value) => {
    navigate(`/products?category=${value}`);
  };

  const [expandedBusinessInfo, setExpandedBusinessInfo] = useState(false);

  return (
      <>
        <footer className="bg-white dark:bg-[#08150d] border-t border-[#e7f3eb] dark:border-[#1d3324] py-8 lg:py-12 font-display">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
            
            {/* Desktop Grid / Mobile Stack */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-10">
              
              {/* 1. Logo & Desc (Always Visible) */}
              <div className="col-span-1 md:col-span-1 mb-6 md:mb-0">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  {logoUrl ? (
                    <img src={logoUrl} alt={displayName} className="h-8 w-auto object-contain" />
                  ) : (
                    <div 
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-contrast"
                      style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                      <span className="material-symbols-outlined text-xl font-bold">local_florist</span>
                    </div>
                  )}
                  <h2 className="text-lg font-extrabold tracking-tight dark:text-white">{displayName}</h2>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed mb-6 hidden md:block">
                  {displayName}은 365일 정성을 다해 가장 신선한 꽃을 전국 3시간 이내에 배송해드리는 프리미엄 꽃배달 서비스입니다.
                </p>
                <div className="flex gap-4">
                  <div className="size-8 rounded-full bg-gray-100 dark:bg-[#1d3324] flex items-center justify-center text-gray-600 dark:text-gray-400">
                    <span className="material-symbols-outlined text-lg">share</span>
                  </div>
                </div>
              </div>

              {/* 2. Shopping Info (Accordion on Mobile) */}
              <div className="md:col-span-1">
                <MobileFooterAccordion title="쇼핑 정보">
                    <ul className="space-y-3 text-sm text-gray-500">
                    <li><button className="hover:text-primary-strong bg-transparent border-none cursor-pointer p-0" onClick={async () => {
                      try { const res = await getCommonPolicyByType('SHIPPING'); setPolicyModal({ isOpen: true, title: '배송안내', content: res.data?.content || '등록된 배송안내가 없습니다.' }); } catch { setPolicyModal({ isOpen: true, title: '배송안내', content: '등록된 배송안내가 없습니다.' }); }
                    }}>배송안내</button></li>
                    <li><button className="hover:text-primary-strong bg-transparent border-none cursor-pointer p-0" onClick={() => navigate('/login')}>주문조회</button></li>
                    <li><button className="hover:text-primary-strong bg-transparent border-none cursor-pointer p-0" onClick={async () => {
                      try { const res = await getCommonPolicyByType('RETURN'); setPolicyModal({ isOpen: true, title: '환불 및 교환', content: res.data?.content || '등록된 환불/교환 정책이 없습니다.' }); } catch { setPolicyModal({ isOpen: true, title: '환불 및 교환', content: '등록된 환불/교환 정책이 없습니다.' }); }
                    }}>환불 및 교환</button></li>
                    <li><button className="hover:text-primary-strong bg-transparent border-none cursor-pointer p-0" onClick={() => setTermsOpen(true)}>이용약관</button></li>
                    </ul>
                </MobileFooterAccordion>
              </div>

              {/* 3. Categories (Accordion on Mobile) */}
              <div className="md:col-span-1">
                <MobileFooterAccordion title="카테고리">
                    <ul className="space-y-3 text-sm text-gray-500">
                    {sortedCategories.slice(0, 5).map((cat) => (
                        <li key={cat.value}>
                        <button 
                            onClick={() => handleCategoryClick(cat.value)} 
                            className="hover:text-primary-strong bg-transparent border-none cursor-pointer p-0 text-left"
                        >
                            {cat.name}
                        </button>
                        </li>
                    ))}
                    <li>
                        <button 
                        onClick={() => navigate('/products')} 
                        className="hover:text-primary-strong font-bold mt-1 bg-transparent border-none cursor-pointer p-0 text-left flex items-center gap-1"
                        >
                        전체보기 <span className="material-symbols-outlined text-xs">arrow_forward</span>
                        </button>
                    </li>
                    </ul>
                </MobileFooterAccordion>
              </div>

              {/* 4. Customer Center (Always Visible Details) */}
              <div className="mt-6 md:mt-0 border-t border-gray-100 dark:border-gray-800 md:border-0 pt-6 md:pt-0">
                <h3 className="font-bold mb-4 dark:text-white text-sm hidden md:block">고객센터</h3>
                <div className="space-y-1.5">
                   <p className="text-sm font-bold md:hidden mb-2">고객센터</p>
                  {csInfo.phone && (
                    <p className="text-2xl font-black text-primary-strong" style={{ color: 'var(--primary-strong)' }}>
                      {csInfo.phone}
                    </p>
                  )}
                  {csInfo.operatingHours && <p className="text-xs text-gray-500">{csInfo.operatingHours}</p>}
                  {csInfo.lunchTime && <p className="text-xs text-gray-500">점심: {csInfo.lunchTime}</p>}
                  {csInfo.holidays && <p className="text-xs text-gray-500">{csInfo.holidays}</p>}
                  {csInfo.email && <p className="text-xs text-gray-500">{csInfo.email}</p>}
                  {csInfo.kakaoId && <p className="text-xs text-gray-500">카카오톡: {csInfo.kakaoId}</p>}
                  {csInfo.note && <p className="text-xs text-gray-400 mt-1">{csInfo.note}</p>}
                </div>
              </div>
            </div>

            {/* Business Info Toggle for Mobile */}
            <div className="mt-8 md:mt-12 pt-8 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 text-center md:text-left space-y-6">

              {/* 판매·배송 주체 고지: 본사(주식회사 플라워 플랫폼)가 실제 판매자 - 모바일에서도 항상 노출 */}
              {sellerInfo?.shopName && (
                <p className="my-3 p-2 text-[11px] leading-relaxed bg-gray-50 dark:bg-gray-800/40 text-gray-600 dark:text-gray-400 rounded">
                  본 사이트(<strong>{sellerInfo.shopName}</strong>)는 주식회사 플라워 플랫폼이 운영하는 판매 채널이며,
                  상품의 판매·배송 및 거래 책임은 주식회사 플라워 플랫폼에 있습니다.
                </p>
              )}

              {/* Mobile View More Button */}
              <div className="md:hidden">
                  <button
                    onClick={() => setExpandedBusinessInfo(!expandedBusinessInfo)}
                    className="flex items-center justify-center w-full gap-1 text-gray-400 py-2"
                  >
                    사업자 정보 {expandedBusinessInfo ? '닫기' : '더보기'}
                    <span className={`material-symbols-outlined text-sm transition-transform ${expandedBusinessInfo ? 'rotate-180' : ''}`}>expand_more</span>
                  </button>
              </div>

              {/* Collapsible Content */}
              <div className={`${expandedBusinessInfo ? 'block' : 'hidden'} md:block space-y-6`}>
                 {/* 본사 정보 (예시) */}
                <div>
                    <p className="font-bold mb-2 text-gray-700 dark:text-gray-300">주식회사 플라워 플랫폼 (본사)</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center md:justify-start leading-relaxed">
                    <span>대표: 곽도현</span>
                    <span className="text-gray-300">|</span>
                    <span>사업자등록번호: 510-88-03725</span>
                    <span className="text-gray-300">|</span>
                    <span>통신판매업신고: 제 2026-대구수성구-0776 호</span>
                    <span className="text-gray-300">|</span>
                    <span>개인정보보호책임자: 곽도현</span>
                    </div>
                    <p className="mt-1">주소: 대구광역시 수성구 청수로26길 46, 1동 8층 801-A호 (두산동)</p>
                </div>

                {/* 판매자(몰 관리자) 정보 - 빈 필드는 자동 숨김 (개인 셀러 지원) */}
                <SellerBusinessInfo
                  seller={sellerInfo}
                  displayName={displayName}
                  align="left"
                />
              </div>

              <p className="pt-4 text-center text-gray-400">
                © {new Date().getFullYear()} {displayName}. All rights reserved.
              </p>
            </div>
          </div>
        </footer>

        <Modal isOpen={policyModal.isOpen} onClose={() => setPolicyModal({ isOpen: false, title: '', content: '' })} title={policyModal.title} size="lg">
          <div className="ck-content prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: sanitizeHtml(policyModal.content) }} />
        </Modal>
        <TermsModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} />
      </>
  );
}

export default SellerFooter;
